/**
 * T-1 · La file de travail du matin — l'écran d'ENTRÉE de la console tuteur.
 *
 * Le changement de fond du Sprint 16 (§10.1) : la console n'ouvre plus sur un
 * tableau de quinze lignes. **Un tableau ne priorise pas, il présente** — s'il
 * faut lire quinze lignes et trancher soi-même, la priorisation n'a pas eu lieu,
 * elle a été rendue à l'humain qu'on prétendait soulager. Ici, trois à six
 * unités déjà ordonnées par le serveur, chacune portant son motif écrit.
 *
 * Trois choses font tenir le KPI « quinze élèves priorisés en moins de trois
 * minutes », et elles sont toutes à l'écran :
 *   1. la file bornée (`MAX_UNITES_FILE`), avec l'excédent ANNONCÉ et non servi ;
 *   2. le motif rédigé + l'ancrage daté, qui rendent la priorité contestable ;
 *   3. le DÉCOMPTE MOTIVÉ des écartés — « 7 avancent », « 3 ont travaillé
 *      hier » — qui permet de faire confiance à la file sans aller vérifier.
 *      C'est pourquoi il n'est pas relégué en bas de page : il est en vis-à-vis.
 *
 * Deux variantes sont traitées au même titre que le cas nominal :
 *   - **la file vide est un BON écran** (le titre vient du serveur), pas un
 *     écran vide : le décompte y explique où en est chaque élève, et deux
 *     choses utiles mais non urgentes sont proposées — toutes deux dérivées de
 *     l'état réel, aucune fabriquée ;
 *   - **le signal de sécurité passe en tête, SEUL**, en pleine largeur, avant
 *     la grille. C'est le seul rouge autorisé de toute la console.
 *
 * Aucun texte de priorisation n'est écrit ici : `titre`, `motif`, `ancrage`,
 * `critere`, `criteresParType`, les titres et explications d'écartés et
 * `excedent.message` viennent tous de l'API et sont rendus tels quels.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  MAX_UNITES_FILE,
  denombrer,
  type EcartDto,
  type ElevesSuivisResponse,
  type FileTuteurResponse,
} from '@silex/shared';

import { api } from '../api/client';
import { ChipTemps } from '../components/ChipTemps';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import {
  formatDate,
  formatDuree,
  formatJourDate,
  libelleTypeUnite,
  pluriel,
} from '../lib/tuteur-format';
import { EmptyState } from '../components/EmptyState';
import {
  aVoirEnPriorite,
  lireUnite,
  reperesDuManuel,
  uniteEnPause,
  type RepereManuel,
} from '../lib/ma-journee-manuel';
import { ancienParcoursEnPause } from '../lib/manuel-vivant';
import { adressesManuelEleve } from '../lib/manuel-tuteur-format';
import { messageErreur, useAsync, type Resource } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';
import { UniteTravailCarte } from '../tuteur/UniteTravailCarte';

/**
 * La voie de recours du tuteur, quand la console ne peut pas enregistrer sa
 * décision. Même adresse que celle employée par `RequireAuth` : un tuteur n'a
 * pas à connaître deux adresses selon l'écran où il se trouve.
 */
const CONTACT_RESPONSABLE = 'info@neth.ca';
const SUJET_CONTESTATION = encodeURIComponent('Silex — priorité de file contestée');

export function MaJourneePage() {
  const { file, rechargerFile, traitees, marquerTraitee, rouvrirUnite } = useTuteur();
  const [reportEnCours, setReportEnCours] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const navigate = useNavigate();
  const [recherche, setRecherche] = useState('');

  /* Φ-MV-7 (décision F-32) — l'ancien parcours en pause : « Ma journée » lit
     AUSSI la liste du manuel, et ne dit plus que ce que le manuel dit
     (`JourneeDuManuel`, plus bas). Hors pause, aucune route du chantier n'est
     appelée et tout ce qui suit est la page d'avant, à l'identique. */
  const enPause = ancienParcoursEnPause();
  const suivis = useAsync(
    useMemo(() => (enPause ? () => api.manuelElevesSuivis() : null), [enPause]),
  );

  const reporter = useCallback(
    async (cle: string) => {
      setErreurAction(null);
      setReportEnCours(cle);
      try {
        await api.reporterUnite({ cle });
        rechargerFile();
      } catch (err) {
        setErreurAction(messageErreur(err));
      } finally {
        setReportEnCours(null);
      }
    },
    [rechargerFile],
  );

  const reprendre = useCallback(
    async (cle: string) => {
      setErreurAction(null);
      try {
        await api.reprendreUnite({ cle });
        rechargerFile();
      } catch (err) {
        setErreurAction(messageErreur(err));
      }
    },
    [rechargerFile],
  );

  /* En pause, la file attend la liste du manuel : sans elle on ne saurait pas
     quelles unités taire, et un « Blocage à traiter » passerait un instant. */
  if (file.status === 'loading' || (enPause && suivis.status === 'loading')) {
    return (
      <div className="content__inner">
        <Chargement quoi="votre file du matin" />
      </div>
    );
  }
  if (file.status === 'error') {
    return (
      <div className="content__inner">
        <ErreurRessource message={file.message} />
      </div>
    );
  }

  const data = file.data;
  if (enPause) {
    return (
      <JourneeDuManuel
        data={data}
        suivis={suivis}
        traitees={traitees}
        reportEnCours={reportEnCours}
        erreurAction={erreurAction}
        surReport={(cle) => void reporter(cle)}
        surReprise={(cle) => void reprendre(cle)}
        surTraitee={marquerTraitee}
        surReouverture={rouvrirUnite}
      />
    );
  }
  /**
   * LOT 3.5 (CON-TUT-JOU-02) — le champ « Rechercher un élève » FILTRE.
   *
   * « Trois essais, 2,5 s d'attente chacun : "Lé" -> la file affiche toujours
   * les quatre unités, Noah compris ; "Zorglub" (aucun élève de ce nom) -> les
   * quatre unités sont toujours là, et AUCUN message n'apparaît. Ni filtrage,
   * ni état vide. Le champ accepte la frappe et ne fait rien. CE QUI REND LE
   * DÉFAUT NET : le même produit fait la même chose CORRECTEMENT sur "Mes
   * élèves". »
   *
   * Le champ n'était pas mort : il attendait `Entrée` pour NAVIGUER vers « Mes
   * élèves ». Un champ posé au-dessus d'une liste, sans bouton, qui ne réagit
   * pas à la frappe, n'annonce rien de tout cela — et personne n'appuie sur
   * Entrée dans un champ qui semble inerte.
   *
   * Il fait donc ce que sa place promet : il filtre la file, sous les yeux,
   * comme celui de « Mes élèves ». Le signal de sécurité N'EST PAS filtrable —
   * il est en tête, seul, et le masquer par une recherche serait le seul cas
   * où un filtre pourrait cacher une urgence.
   */
  const q = recherche.trim().toLocaleLowerCase('fr-CA');
  const correspond = (u: (typeof data.unites)[number]): boolean =>
    q === '' || u.elevePrenom.toLocaleLowerCase('fr-CA').includes(q);

  const signaux = data.unites.filter((u) => u.type === 'signal_securite');
  const autres = data.unites.filter((u) => u.type !== 'signal_securite').filter(correspond);
  const filtreActif = q !== '';
  const masquees = data.unites.filter((u) => u.type !== 'signal_securite').length - autres.length;
  const nbTraitees = data.unites.filter((u) => traitees.has(u.cle)).length;
  const restantes = data.unites.length - nbTraitees;

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          {/* Le titre est rédigé par le serveur : « Trois choses aujourd'hui »,
              ou l'état vide. On ne le recompose pas côté client. */}
          <h1>{data.titre}</h1>
          <p className="page-sub">
            {formatJourDate(data.genereeLe)} ·{' '}
            {pluriel(data.nbElevesSuivis, 'élève suivi', 'élèves suivis')}
            {data.unites.length > 0
              ? ` · ${restantes === 0 ? 'tout est traité' : `${restantes} à traiter`}`
              : ''}
          </p>
        </div>
        <form
          className="topbar-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            // `Entrée` reste un raccourci utile : elle ouvre « Mes élèves »
            // sur la même recherche, pour voir TOUS ses élèves et pas
            // seulement ceux qui demandent une décision aujourd'hui.
            navigate(`/eleves?q=${encodeURIComponent(recherche.trim())}`);
          }}
        >
          <label className="field field--inline">
            <span className="lbl sr-only">Filtrer la file par élève</span>
            <input
              className="control"
              type="search"
              placeholder="Filtrer par élève…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              aria-describedby="journee-recherche-aide"
            />
          </label>
          <span id="journee-recherche-aide" className="sr-only">
            La file se filtre à la frappe. Entrée ouvre « Mes élèves » sur la même recherche.
          </span>
        </form>
      </div>

      {erreurAction ? <ErreurRessource message={erreurAction} /> : null}

      {/* Le signal de sécurité : en tête, seul, pleine largeur, non reportable. */}
      {signaux.length > 0 ? (
        <section className="wq wq--securite" aria-label="Avant tout le reste">
          {signaux.map((unite) => (
            <UniteTravailCarte
              key={unite.cle}
              unite={unite}
              traitee={traitees.has(unite.cle)}
              enCours={reportEnCours === unite.cle}
              onReporter={(cle) => void reporter(cle)}
              onMarquerTraitee={marquerTraitee}
              onRouvrir={rouvrirUnite}
            />
          ))}
        </section>
      ) : null}

      <div className="tuteur-grid">
        <div>
          {autres.length > 0 ? (
            <div className="wq">
              {autres.map((unite) => (
                <UniteTravailCarte
                  key={unite.cle}
                  unite={unite}
                  traitee={traitees.has(unite.cle)}
                  enCours={reportEnCours === unite.cle}
                  onReporter={(cle) => void reporter(cle)}
                  onMarquerTraitee={marquerTraitee}
                  onRouvrir={rouvrirUnite}
                />
              ))}
            </div>
          ) : null}

          {/* L'état vide du FILTRE, distinct de celui de la file : dire « rien
              à faire aujourd'hui » à un tuteur qui vient de taper un nom
              serait faux. C'est l'écart exact relevé sur « Zorglub ». */}
          {filtreActif && autres.length === 0 && data.unites.length > 0 ? (
            <EmptyState title="Aucune unité pour cette recherche">
              {masquees > 0
                ? `${pluriel(masquees, 'unité', 'unités')} de la file ne correspondent pas à « ${recherche.trim()} ». Effacez la recherche pour les revoir.`
                : `Aucun élève de votre file ne correspond à « ${recherche.trim()} ».`}
            </EmptyState>
          ) : null}

          {data.unites.length === 0 ? <FileVide data={data} /> : null}

          {data.unites.length > 0 && restantes === 0 ? (
            <div className="card callout--ok" role="status">
              <h3>La file du jour est traitée.</h3>
              <p className="note-line">
                Une unité close par une décision enregistrée ne revient pas. Les autres portent une
                marque locale à cet onglet et reviendront demain si leur cause demeure.
              </p>
            </div>
          ) : null}

          <Critere data={data} />
        </div>

        <div className="stack">
          <Ecartes data={data} />
          {data.excedent.nbUnites > 0 ? (
            <div className="card">
              <h3>Ce qui n'entre pas dans la journée</h3>
              {/* Message rédigé par le serveur : « 4 autres points, moins urgents ». */}
              <p className="card-sub">{data.excedent.message}</p>
              <p className="note-line">File bornée à {MAX_UNITES_FILE} unités.</p>
            </div>
          ) : null}
          {data.reportes.length > 0 ? (
            <div className="card wq-item wq-item--reporte">
              <div>
                <span className="stage-label">Reporté</span>
                <div className="stack-sm">
                  {data.reportes.map((r) => (
                    <div key={r.unite.cle} className="reporte-ligne">
                      <p className="wq-motif">
                        <b>{r.unite.elevePrenom}</b> — {libelleTypeUnite(r.unite.type)}.{' '}
                        {r.unite.motif}
                      </p>
                      <p className="wq-ancrage">
                        Reporté le {formatDate(r.reporteLe)} · revient le {formatDate(r.reprendLe)}
                        {r.motifReport ? ` · ${r.motifReport}` : ''}
                      </p>
                      <div className="wq-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => void reprendre(r.unite.cle)}
                        >
                          Le faire maintenant
                        </button>
                        <ChipTemps aDroite>{formatDuree(r.unite.dureeEstimeeMinutes)}</ChipTemps>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          <Semaine data={data} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------- l'ancien parcours en pause (F-32) */

/**
 * « Ma journée » d'un tuteur dont la capacité « manuel vivant » est allumée,
 * l'ancien parcours en pause (Φ-MV-7 : `UX-7-03`, décision F-32).
 *
 * Elle montre, dans cet ordre : le signal de sécurité (toujours, seul, en
 * tête) ; ce qui reste de la file — une évaluation qui attend un verdict
 * humain ; puis LES ÉLÈVES À VOIR EN PRIORITÉ selon le manuel, dans l'ordre et
 * avec la raison que le serveur écrit pour « Mes élèves ». Elle ne montre plus
 * les unités « blocage » et « à relancer » de l'ancien suivi pour un élève qui
 * a une fiche du manuel, ni le décompte des écartés, le critère et la tuile de
 * la semaine, qui ne parlent que de l'ancienne file.
 *
 * Le titre n'est plus celui du serveur (« Trois choses aujourd'hui » compterait
 * des unités que l'écran ne montre pas) : c'est le nom de la page.
 */
export function JourneeDuManuel({
  data,
  suivis,
  traitees,
  reportEnCours,
  erreurAction,
  surReport,
  surReprise,
  surTraitee,
  surReouverture,
}: {
  data: FileTuteurResponse;
  suivis: Resource<ElevesSuivisResponse>;
  traitees: ReadonlySet<string>;
  reportEnCours: string | null;
  erreurAction: string | null;
  surReport: (cle: string) => void;
  surReprise: (cle: string) => void;
  surTraitee: (cle: string) => void;
  surReouverture: (cle: string) => void;
}) {
  const [recherche, setRecherche] = useState('');
  const reperes = useMemo(
    () =>
      suivis.status === 'ready' ? reperesDuManuel(suivis.data) : new Map<string, RepereManuel>(),
    [suivis],
  );
  const q = recherche.trim().toLocaleLowerCase('fr-CA');
  const correspond = (prenom: string): boolean =>
    q === '' || prenom.toLocaleLowerCase('fr-CA').includes(q);

  const unites = data.unites.filter((u) => !uniteEnPause(u, reperes.get(u.cible.eleveId)));
  /* Le signal de sécurité n'est pas filtrable : une recherche ne cache pas une urgence. */
  const signaux = unites.filter((u) => u.type === 'signal_securite');
  const autres = unites
    .filter((u) => u.type !== 'signal_securite')
    .filter((u) => correspond(u.elevePrenom));
  const priorites = aVoirEnPriorite(reperes).filter((r) => correspond(r.prenom));
  const reportes = data.reportes.filter(
    (r) => !uniteEnPause(r.unite, reperes.get(r.unite.cible.eleveId)),
  );

  const carte = (unite: (typeof unites)[number]) => (
    <UniteTravailCarte
      key={unite.cle}
      unite={unite}
      traitee={traitees.has(unite.cle)}
      enCours={reportEnCours === unite.cle}
      onReporter={surReport}
      onMarquerTraitee={surTraitee}
      onRouvrir={surReouverture}
      lecture={lireUnite(unite, reperes.get(unite.cible.eleveId))}
    />
  );

  return (
    <div className="content__inner manuel-tuteur" data-testid="journee-du-manuel">
      <div className="dash-topbar">
        <div>
          <h1>Ma journée</h1>
          <p className="page-sub">
            {formatJourDate(data.genereeLe)} ·{' '}
            {pluriel(data.nbElevesSuivis, 'élève suivi', 'élèves suivis')}
            {suivis.status === 'ready'
              ? ` · ${suivis.data.aVoirEnPriorite} à voir en priorité cette semaine`
              : ''}
          </p>
        </div>
        <div className="topbar-search" role="search">
          <label className="field field--inline">
            <span className="lbl sr-only">Filtrer par élève</span>
            <input
              className="control"
              type="search"
              placeholder="Filtrer par élève…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </label>
        </div>
      </div>

      {erreurAction ? <ErreurRessource message={erreurAction} /> : null}
      {suivis.status === 'error' ? <ErreurRessource message={suivis.message} /> : null}

      {signaux.length > 0 ? (
        <section className="wq wq--securite" aria-label="Avant tout le reste">
          {signaux.map(carte)}
        </section>
      ) : null}

      <div className="tuteur-grid">
        <div>
          {autres.length > 0 ? <div className="wq">{autres.map(carte)}</div> : null}

          {priorites.length > 0 ? (
            <section
              className="wq"
              aria-label="À voir en priorité cette semaine"
              data-testid="journee-priorites"
            >
              {priorites.map((r, i) => (
                <article key={r.eleveId} className="card wq-item" data-testid="journee-priorite">
                  <span className="wq-ord" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <div className="wq-head">
                      <span className="wq-eleve">{r.prenom}</span>
                      {r.etiquette === null ? null : (
                        <span className="badge badge--a-revoir">{r.etiquette}</span>
                      )}
                    </div>
                    {/* Rédigé par le serveur pour « Mes élèves » — affiché tel quel. */}
                    <p className="wq-motif">{r.pourquoi}</p>
                    <div className="wq-actions">
                      <Link
                        className="btn btn--primary btn--sm"
                        to={adressesManuelEleve.preparation(r.eleveId)}
                      >
                        Préparer la séance
                      </Link>
                      <Link
                        className="btn btn--quiet btn--sm"
                        to={adressesManuelEleve.fiche(r.eleveId)}
                        data-testid="journee-priorite-fiche"
                      >
                        Ouvrir sa fiche
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          ) : null}

          {suivis.status === 'ready' && autres.length === 0 && priorites.length === 0 ? (
            q === '' ? (
              <div className="card" data-testid="journee-sans-priorite">
                <p className="lede">
                  {pluriel(data.nbElevesSuivis, 'élève suivi', 'élèves suivis')} : aucun n’est à
                  voir en priorité cette semaine.
                </p>
              </div>
            ) : (
              <EmptyState title="Aucun élève pour cette recherche">
                Effacez la recherche pour revoir votre journée.
              </EmptyState>
            )
          ) : null}
        </div>

        <div className="stack">
          <div className="card">
            <div className="row-between">
              <h3>Tous vos élèves</h3>
              <Link to="/eleves">Ouvrir la liste</Link>
            </div>
            <p className="card-sub">
              {suivis.status === 'ready'
                ? `${denombrer(suivis.data.eleves.length, 'élève')}, dans l’ordre où les voir, chacun avec sa raison.`
                : 'Dans l’ordre où les voir, chacun avec sa raison.'}
            </p>
          </div>
          {reportes.length > 0 ? (
            <div className="card wq-item wq-item--reporte">
              <div>
                <span className="stage-label">Reporté</span>
                <div className="stack-sm">
                  {reportes.map((r) => (
                    <div key={r.unite.cle} className="reporte-ligne">
                      <p className="wq-motif">
                        <b>{r.unite.elevePrenom}</b> — {libelleTypeUnite(r.unite.type)}.{' '}
                        {lireUnite(r.unite, reperes.get(r.unite.cible.eleveId)).motif}
                      </p>
                      <p className="wq-ancrage">
                        Reporté le {formatDate(r.reporteLe)} · revient le {formatDate(r.reprendLe)}
                      </p>
                      <div className="wq-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => surReprise(r.unite.cle)}
                        >
                          Le faire maintenant
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- le critère */

/**
 * Le critère de priorisation, affiché tel qu'il est écrit dans le code, et le
 * lien de contestation. Un tri opaque n'est jamais adopté par un professionnel ;
 * un tri dont le critère est écrit peut être contesté, donc accepté (PRIV-10).
 */
function Critere({ data }: { data: FileTuteurResponse }) {
  const typesPresents = [...new Set(data.unites.map((u) => u.type))];
  return (
    <div className="card card--calm critere-card">
      <h3>
        {data.unites.length > 0
          ? 'Pourquoi ces unités-là, et pas les autres'
          : 'Le critère qui remplit cette file'}
      </h3>
      <p className="critere-texte">{data.critere}</p>
      {typesPresents.length > 0 ? (
        <ul className="critere-liste">
          {typesPresents.map((type) => (
            <li key={type}>
              <b>{libelleTypeUnite(type)}</b> — {data.criteresParType[type]}
            </li>
          ))}
        </ul>
      ) : null}
      {/* LOT 3.5 (CON-TUT-JOU-08) — « LE LIEN NE FAIT PAS CE QUE LA PHRASE
          ANNONCE. "Signaler une priorité que vous jugez fausse" est un
          `<a href="/eleves">` : il envoie le tuteur sur le tableau "Mes
          élèves". Aucun `mailto:`, aucun formulaire, aucun destinataire. »

          Le texte disait pourtant la vérité : la contestation « passe
          aujourd'hui par votre responsable ». Le lien, lui, ne menait pas au
          responsable. Un `mailto:` mène là où la phrase dit — c'est la même
          voie de recours que la console emploie déjà quand elle ne peut rien
          faire elle-même (`RequireAuth`). Rien n'est simulé : aucun
          enregistrement n'est prétendu. */}
      <p className="note-line">
        Critère affiché tel qu'il est écrit dans le code. Vous pouvez le contester :{' '}
        <a href={`mailto:${CONTACT_RESPONSABLE}?subject=${SUJET_CONTESTATION}`}>
          signaler une priorité que vous jugez fausse
        </a>{' '}
        — la contestation n'est pas encore enregistrée par l'API, elle passe aujourd'hui par votre
        responsable, à cette adresse.
      </p>
    </div>
  );
}

/* ---------------------------------------------------- le décompte motivé */

function LigneEcart({ ecart }: { ecart: EcartDto }) {
  return (
    <div className="li">
      <div className="li-main">
        {/* « 7 avancent » / « 3 ont travaillé hier » — rédigé par le serveur. */}
        <div className="li-title">{ecart.titre}</div>
        <div className="li-sub">
          {ecart.explication}
          {ecart.apercu.length > 0
            ? ` ${ecart.apercu.map((e) => e.prenom).join(', ')}${
                ecart.apercu.length < ecart.nbEleves
                  ? ` et ${ecart.nbEleves - ecart.apercu.length} autre${
                      ecart.nbEleves - ecart.apercu.length > 1 ? 's' : ''
                    }.`
                  : '.'
              }`
            : ''}
        </div>
      </div>
    </div>
  );
}

function Ecartes({ data }: { data: FileTuteurResponse }) {
  const nbEcartes = data.ecartes.reduce((total, e) => total + e.nbEleves, 0);
  if (nbEcartes === 0) return null;
  return (
    <div className="card">
      <div className="row-between">
        <h3>Les {nbEcartes} autres élèves</h3>
        <Link to="/eleves">Ouvrir la liste</Link>
      </div>
      <p className="card-sub">Aucun d'eux ne demande de décision ce matin.</p>
      <div className="list list--flush">
        {data.ecartes.map((ecart) => (
          <LigneEcart key={ecart.motif} ecart={ecart} />
        ))}
      </div>
      <p className="note-line">
        Ces paniers sont exclusifs et exhaustifs : avec les élèves porteurs d'une unité, ils
        couvrent tout votre périmètre.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ la semaine */

function Semaine({ data }: { data: FileTuteurResponse }) {
  return (
    <div className="card">
      <h3>Votre semaine</h3>
      <p className="card-sub">Depuis le {formatJourDate(data.semaine.depuisLe)}</p>
      <div className="stat-tile-row">
        <div className="stat-tile">
          <span className="stat-tile__value">{data.semaine.nbEvaluationsRevues}</span>
          <span className="stat-tile__label">
            évaluation{data.semaine.nbEvaluationsRevues > 1 ? 's' : ''} revue
            {data.semaine.nbEvaluationsRevues > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- la file vide */

/**
 * « Rien qui demande votre attention aujourd'hui » — un BON écran. Le titre
 * vient du serveur ; le corps est le décompte motivé, qui répond à la seule
 * question que pose une file vide : « vraiment, personne ? ».
 */
function FileVide({ data }: { data: FileTuteurResponse }) {
  return (
    <div className="stack">
      <div className="card">
        <p className="lede">
          {pluriel(data.nbElevesSuivis, 'élève suivi', 'élèves suivis')} : aucune évaluation
          n'attend de verdict, aucun savoir n'est repris sans progresser.
        </p>
        <hr className="sep-soft" />
        <p className="card-sub">Si vous avez du temps, deux choses utiles mais non urgentes :</p>
        <div className="row wrap file-vide-actions">
          <Link className="btn btn--ghost btn--sm" to="/eleves">
            Préparer une séance depuis « Mes élèves »
          </Link>
          <Link className="btn btn--ghost btn--sm" to="/rapports">
            Relire un rapport avant qu'il ne parte
          </Link>
        </div>
      </div>
    </div>
  );
}
